import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, MoreVertical, Eye, Edit, Trash2, Send, CheckCircle, XCircle, FileText, Search, X, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { quotesApi, clientsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { usePermissions } from '@/contexts/PermissionContext'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { formatDate, formatCurrency } from '@/lib/utils'
import type { QuoteStatus } from '@/types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const statusColors: Record<QuoteStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  EXPIRED: 'bg-orange-100 text-orange-800',
  CONVERTED: 'bg-purple-100 text-purple-800',
}

const ALL_STATUSES = 'ALL_STATUSES'
const ALL_CLIENTS = 'ALL_CLIENTS'

export default function QuotesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [clientSearchOpen, setClientSearchOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Filter form state (what user is typing/selecting)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClientId, setSelectedClientId] = useState<string>(ALL_CLIENTS)
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES)

  // Applied filters state (what is actually used for filtering)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [appliedClientId, setAppliedClientId] = useState<string>(ALL_CLIENTS)
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>(ALL_STATUSES)

  // Fetch clients for dropdown
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getAllLegacy(),
  })

  const { data: quotes = [], isLoading, error } = useQuery({
    queryKey: ['quotes'],
    queryFn: () => quotesApi.getAll({}),
  })

  // Filter quotes based on APPLIED filters (client-side filtering)
  const filteredQuotes = useMemo(() => {
    return quotes.filter(quote => {
      // Search term filter (quote number or client name)
      if (appliedSearchTerm) {
        const searchLower = appliedSearchTerm.toLowerCase()
        const matchesNumber = quote.quote_number.toLowerCase().includes(searchLower)
        const matchesClient = quote.client_name.toLowerCase().includes(searchLower)
        if (!matchesNumber && !matchesClient) return false
      }

      // Client filter
      if (appliedClientId !== ALL_CLIENTS && quote.client_id !== appliedClientId) {
        return false
      }

      // Status filter
      if (appliedStatusFilter !== ALL_STATUSES && quote.status !== appliedStatusFilter) {
        return false
      }

      return true
    })
  }, [quotes, appliedSearchTerm, appliedClientId, appliedStatusFilter])

  // Pagination calculations
  const totalRecords = filteredQuotes.length
  const totalPages = Math.ceil(totalRecords / pageSize)

  const paginatedQuotes = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredQuotes.slice(startIndex, endIndex)
  }, [filteredQuotes, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  // Apply filters when Search is clicked
  const applyFilters = () => {
    setAppliedSearchTerm(searchTerm)
    setAppliedClientId(selectedClientId)
    setAppliedStatusFilter(statusFilter)
    setCurrentPage(1)
  }

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: quotesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      toast.success('Quote deleted successfully')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete quote'
      toast.error(errorMessage)
    },
  })

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: QuoteStatus }) =>
      quotesApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      toast.success('Quote status updated')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update quote status'
      toast.error(errorMessage)
    },
  })

  // Get selected client name for display
  const selectedClient = selectedClientId !== ALL_CLIENTS ? clients.find(c => c.client_id === selectedClientId) : null

  // Check if any filters are applied (based on applied filters)
  const hasActiveFilters = appliedSearchTerm || appliedClientId !== ALL_CLIENTS || appliedStatusFilter !== ALL_STATUSES

  const clearAllFilters = () => {
    setSearchTerm('')
    setSelectedClientId(ALL_CLIENTS)
    setStatusFilter(ALL_STATUSES)
    setAppliedSearchTerm('')
    setAppliedClientId(ALL_CLIENTS)
    setAppliedStatusFilter(ALL_STATUSES)
    setCurrentPage(1)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t('quotes.loadingQuotes')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">{t('quotes.failedToLoad')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between -mt-2">
        <div>
          <h1 className="text-2xl font-bold">{t('quotes.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('quotes.manageQuotes')}
          </p>
        </div>
        {canAddEdit('QUOTES') && (
          <Link to="/billing/quotes/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('quotes.createQuote')}
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-[300px]">
          <Input
            placeholder={t('quotes.searchQuotes')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
        </div>

        {/* Client */}
        <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={clientSearchOpen}
              className="w-[180px] justify-between font-normal"
            >
              {selectedClient ? selectedClient.client_name : t('billing.allClients')}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder={t('billing.searchClients')} />
              <CommandList>
                <CommandEmpty>{t('billing.noClientFound')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="__all__"
                    onSelect={() => {
                      setSelectedClientId(ALL_CLIENTS)
                      setClientSearchOpen(false)
                    }}
                  >
                    {t('billing.allClients')}
                  </CommandItem>
                  {clients.map((client) => (
                    <CommandItem
                      key={client.client_id}
                      value={client.client_name}
                      onSelect={() => {
                        setSelectedClientId(client.client_id)
                        setClientSearchOpen(false)
                      }}
                    >
                      {client.client_name}
                      {client.client_email && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {client.client_email}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Status */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('billing.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('billing.allStatuses')}</SelectItem>
            <SelectItem value="DRAFT">{t('billing.draft')}</SelectItem>
            <SelectItem value="SENT">{t('billing.sent')}</SelectItem>
            <SelectItem value="ACCEPTED">{t('billing.accepted')}</SelectItem>
            <SelectItem value="REJECTED">{t('billing.rejected')}</SelectItem>
            <SelectItem value="EXPIRED">{t('billing.expired')}</SelectItem>
            <SelectItem value="CONVERTED">{t('billing.converted')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Search Button */}
        <Button onClick={applyFilters}>
          <Search className="mr-2 h-4 w-4" />
          {t('common.search')}
        </Button>

        {/* Clear Button */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      {quotes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('quotes.createFirstQuote')}
            </div>
            {canAddEdit('QUOTES') && (
              <Link to="/billing/quotes/new">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('quotes.createQuote')}
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : filteredQuotes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('quotes.noMatchingQuotes')}
            </div>
            <Button variant="outline" onClick={clearAllFilters}>
              <X className="mr-2 h-4 w-4" />
              {t('common.reset')} {t('common.filters')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col h-[calc(100vh-240px)]">
          <Card className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>{t('quotes.quoteNumber')}</TableHead>
                    <TableHead>{t('billing.client')}</TableHead>
                    <TableHead>{t('quotes.quoteDate')}</TableHead>
                    <TableHead>{t('quotes.validUntil')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="text-right">{t('billing.amount')}</TableHead>
                    <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedQuotes.map((quote) => (
                    <TableRow key={quote.quote_id}>
                      <TableCell className="font-medium">{quote.quote_number}</TableCell>
                      <TableCell>{quote.client_name}</TableCell>
                      <TableCell>{formatDate(quote.quote_date)}</TableCell>
                      <TableCell>{formatDate(quote.valid_until)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[quote.status]} variant="secondary">
                          {t(`billing.${quote.status.toLowerCase()}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(quote.total_amount, quote.currency)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/billing/quotes/${quote.quote_id}`)}>
                              <Eye className="mr-2 h-4 w-4" />
                              {t('quotes.viewQuote')}
                            </DropdownMenuItem>
                            {canAddEdit('QUOTES') && (quote.status === 'DRAFT' || quote.status === 'SENT') && (
                              <DropdownMenuItem onClick={() => navigate(`/billing/quotes/${quote.quote_id}/edit`)}>
                                <Edit className="mr-2 h-4 w-4" />
                                {t('quotes.editQuote')}
                              </DropdownMenuItem>
                            )}
                            {canAddEdit('QUOTES') && (
                              <>
                                <DropdownMenuSeparator />
                                {quote.status === 'DRAFT' && (
                                  <DropdownMenuItem onClick={() => updateStatusMutation.mutate({ id: quote.quote_id, status: 'SENT' })}>
                                    <Send className="mr-2 h-4 w-4" />
                                    {t('quotes.markAsSent')}
                                  </DropdownMenuItem>
                                )}
                                {quote.status === 'SENT' && (
                                  <>
                                    <DropdownMenuItem onClick={() => updateStatusMutation.mutate({ id: quote.quote_id, status: 'ACCEPTED' })}>
                                      <CheckCircle className="mr-2 h-4 w-4" />
                                      {t('quotes.markAsAccepted')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => updateStatusMutation.mutate({ id: quote.quote_id, status: 'REJECTED' })}>
                                      <XCircle className="mr-2 h-4 w-4" />
                                      {t('quotes.markAsRejected')}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {(quote.status === 'ACCEPTED' || quote.status === 'SENT') && (
                                  <DropdownMenuItem onClick={() => navigate(`/billing/quotes/${quote.quote_id}/convert`)}>
                                    <FileText className="mr-2 h-4 w-4" />
                                    {t('quotes.convertToInvoice')}
                                  </DropdownMenuItem>
                                )}
                                {quote.status === 'DRAFT' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={() => deleteMutation.mutate(quote.quote_id)}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      {t('quotes.deleteQuote')}
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination Controls */}
            <div className="flex items-center gap-3 px-4 py-2 border-t">
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[70px] h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                {startRecord}-{endRecord} {t('common.of')} {totalRecords}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

